import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navigation, FileText, Info, BellPlus, Archive } from "lucide-react";
import { toast } from "sonner";
import { api, pickActive } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";
import { TachoWidget } from "@/components/TachoWidget";
import { StatusButtons } from "@/components/StatusButtons";
import { StatusBadge } from "@/components/StatusBadge";
import { OrderCard } from "@/components/OrderCard";
import { BigButton } from "@/components/BigButton";
import { t, t as tr, locale } from "@/i18n";

const ActiveTrip = ({ trip, onChange }) => {
  const nav = useNavigate();
  return (
    <section className="nv-card p-5 sm:p-6 border-[#4accff]/50" data-testid="active-trip">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="nv-eyebrow">{t("Viaggio attivo · {code}", { code: trip.code })}</p>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold uppercase leading-tight mt-1">
            {trip.pickup.name} <span className="text-[#4accff]">→</span> {trip.delivery.name}
          </h2>
        </div>
        <StatusBadge status={trip.status} testId="active-trip-status" />
      </div>
      <p className="text-slate-400 mt-2">{t(trip.vehicle.type)} · {t(trip.goods.description)} · {trip.goods.weight_kg.toLocaleString(locale())} kg</p>
      <p className="nv-eyebrow mt-6 mb-3">{t("Stato del viaggio")}</p>
      <StatusButtons trip={trip} onChange={onChange} />
      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        <BigButton testId="active-navigate-btn" icon={Navigation} color="#4accff" label={t("Naviga")} sub={t("Avvia il navigatore")} onClick={() => nav(`/navigatore?trip=${trip.id}`)} />
        <BigButton testId="active-docs-btn" icon={FileText} variant="outline" color="#ffffff" label={t("DDT e CMR")} sub={t("Documenti")} onClick={() => nav(`/viaggio/${trip.id}#documenti`)} />
        <BigButton testId="active-details-btn" icon={Info} variant="outline" color="#ef0298" label={t("Dettagli")} sub={t("Tutte le informazioni")} onClick={() => nav(`/viaggio/${trip.id}`)} />
      </div>
    </section>
  );
};

export default function Home() {
  const [trips, setTrips] = useState(null);
  const known = useRef(null);

  const load = async () => {
    const { data } = await api.get("/trips");
    const ids = new Set(data.map((t) => t.id));
    if (known.current) {
      data.filter((t) => !known.current.has(t.id)).forEach((t) => toast(tr("Nuovo ordine dal vettore: {code}", { code: t.code }), { description: `${t.pickup.name} → ${t.delivery.name}` }));
    }
    known.current = ids;
    setTrips(data);
  };

  usePoll(() => load().catch(() => {}), 8000);

  const simulate = async () => {
    await api.post("/demo/new-order");
    load();
  };

  if (!trips) return <div className="p-6 text-slate-400">{tr("Caricamento…")}</div>;
  const active = pickActive(trips);
  const inbox = trips.filter((t) => t.status === "assegnato");
  const done = trips.filter((t) => t.status === "arrivato");

  return (
    <div className="max-w-7xl mx-auto p-4 lg:p-8 grid lg:grid-cols-12 gap-6 items-start">
      <div className="lg:col-span-7 lg:col-start-1 lg:row-start-1">
        {active ? <ActiveTrip trip={active} onChange={load} /> : (
          <div className="nv-card p-6 text-slate-300" data-testid="no-active-trip">{tr("Nessun viaggio attivo. Accetta un ordine qui sotto per iniziare.")}</div>
        )}
      </div>
      <aside className="lg:col-span-5 lg:col-start-8 lg:row-start-1 lg:row-span-2 space-y-6">
        <TachoWidget />
        {trips.filter((t) => ACTIVE_OTHERS(t, active)).map((t) => <OrderCard key={t.id} trip={t} onChange={load} />)}
        {done.length > 0 && (
          <section className="nv-card p-5" data-testid="done-trips">
            <p className="nv-eyebrow flex items-center gap-2"><Archive className="h-4 w-4" /> {tr("Viaggi completati")}</p>
            {done.map((t) => <p key={t.id} className="mt-2 text-slate-300">{t.code} · {t.pickup.name} → {t.delivery.name}</p>)}
          </section>
        )}
      </aside>
      <div className="lg:col-span-7 lg:col-start-1 lg:row-start-2">
        <section data-testid="orders-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-2xl font-extrabold uppercase tracking-wide">{tr("Ordini dal vettore")} <span className="text-[#ef0298]" data-testid="inbox-count">({inbox.length})</span></h2>
            <span className="text-xs text-slate-400 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#10b981] nv-blink" /> {tr("Aggiornamento in tempo reale")}</span>
          </div>
          <div className="space-y-4">
            {inbox.length === 0 && <p className="text-slate-400" data-testid="inbox-empty">{tr("Nessun nuovo ordine.")}</p>}
            {inbox.map((t) => <OrderCard key={t.id} trip={t} onChange={load} />)}
          </div>
          <button data-testid="simulate-order-btn" onClick={simulate} className="mt-4 flex items-center gap-2 text-sm text-slate-400 underline">
            <BellPlus className="h-4 w-4" /> {tr("Simula un nuovo ordine dal vettore (demo)")}
          </button>
        </section>
      </div>
    </div>
  );
}

const ACTIVE_OTHERS = (t, active) => t.status !== "assegnato" && t.status !== "arrivato" && t.id !== active?.id;
